import { useQuery } from '@tanstack/react-query';
import { useDataProvider } from 'react-admin';
import { buildBanEvasionView, type BanEvasionView } from './banEvasionModel';
import type { Market, User } from '../dataProvider/types';

/**
 * The Ban-evasion view, read through the aggregation seam (`buildBanEvasionView`),
 * NOT computed in the component. For v1 it pulls raw users from the mock provider
 * and derives the suspected links client-side; the real-data migration replaces the
 * fetch-and-derive here with a server-side endpoint and the surface does not change.
 * Same seam idiom as useFraudQueue / useSuspensions.
 *
 * INVESTIGATIVE-GLOBAL: it does NOT read the region switcher. It is global by
 * default and the match runs cross-market; the optional `regionFilter` (the
 * surface's own facet) narrows by the ANCHOR's market by exact value.
 */

const ALL = {
  pagination: { page: 1, perPage: 100_000 },
  sort: { field: 'id', order: 'ASC' as const },
  filter: {},
};

export const useBanEvasion = (
  regionFilter: Market | null,
): {
  view: BanEvasionView | null;
  isLoading: boolean;
} => {
  const dataProvider = useDataProvider();

  const { data, isLoading } = useQuery({
    queryKey: ['ban-evasion', regionFilter],
    queryFn: async () => {
      const users = await dataProvider.getList('users', ALL);
      return buildBanEvasionView(
        { users: users.data as unknown as User[] },
        { regionFilter },
      );
    },
  });

  return { view: data ?? null, isLoading };
};
